import { useState } from 'react';
import PropTypes from 'prop-types';
import { BIBLE_BOOKS } from '../../utils/bibleBooks';
import './BookSelector.css';

const TESTAMENTS = [
  { key: 'Old', label: 'Old' },
  { key: 'New', label: 'New' }
];

/**
 * Whole books as a chip grid: one Old/New toggle, a search box, and an
 * All/None shortcut for the testament in view. Selected names are shown by
 * the chips themselves, so there is no separate summary.
 */
const BookSelector = ({ selectedBooks, onBooksChange }) => {
  const [testament, setTestament] = useState('Old');
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  // Searching looks across both testaments so a book is never "missing".
  const visible = BIBLE_BOOKS.filter(b =>
    query ? b.name.toLowerCase().includes(query) : b.testament === testament
  );
  const visibleNames = visible.map(b => b.name);
  const allVisibleSelected = visibleNames.length > 0 && visibleNames.every(n => selectedBooks.includes(n));

  const toggle = (name) => {
    onBooksChange(
      selectedBooks.includes(name)
        ? selectedBooks.filter(n => n !== name)
        : [...selectedBooks, name]
    );
  };

  const toggleVisible = () => {
    onBooksChange(
      allVisibleSelected
        ? selectedBooks.filter(n => !visibleNames.includes(n))
        : [...new Set([...selectedBooks, ...visibleNames])]
    );
  };

  return (
    <div className="book-picker">
      <div className="book-picker-bar">
        <div className="book-picker-tabs" role="tablist" aria-label="Testament">
          {TESTAMENTS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={!query && testament === key}
              className={`book-picker-tab ${!query && testament === key ? 'active' : ''}`}
              onClick={() => { setTestament(key); setSearch(''); }}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="book-picker-search"
          placeholder="Search"
          aria-label="Search books"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="book-picker-link" onClick={toggleVisible} disabled={visible.length === 0}>
          {allVisibleSelected ? 'None' : 'All'}
        </button>
      </div>

      <div className="book-chips">
        {visible.map(b => {
          const selected = selectedBooks.includes(b.name);
          return (
            <button
              key={b.name}
              type="button"
              className={`book-chip ${selected ? 'selected' : ''}`}
              aria-pressed={selected}
              onClick={() => toggle(b.name)}
            >
              {b.name}
            </button>
          );
        })}
        {visible.length === 0 && <p className="book-picker-empty">No book matches “{search}”.</p>}
      </div>

      {selectedBooks.length > 0 && (
        <button type="button" className="book-picker-clear" onClick={() => onBooksChange([])}>
          Clear {selectedBooks.length} selected
        </button>
      )}
    </div>
  );
};

BookSelector.propTypes = {
  selectedBooks: PropTypes.arrayOf(PropTypes.string).isRequired,
  onBooksChange: PropTypes.func.isRequired
};

export default BookSelector;
